import { NgModule, inject } from '@angular/core';
import { Router, RouterModule, Routes } from '@angular/router';

import { AuthService } from 'src/services/auth.service';

import { AuthGuard } from '../auth.guard';
import { CompanyPrivacyGuard } from '../company-privacy.guard';
import { INBOX_TYPES } from '../constants';

import { CxDashboardComponent } from './cx-dashboard/cx-dashboard.component';
import { CxHomeComponent } from './cx-home.component';

const knowledgeGapAnalysisGuard = () => {
  const authService = inject(AuthService);
  const router = inject(Router);

  if (authService.tenant?.featureFlags?.knowledgeGapAnalysis === true) {
    return true;
  }

  return router.createUrlTree(['/dashboard/knowledge/articles']);
};

const routes: Routes = [
  {
    path: '',
    component: CxHomeComponent,
    children: [
      {
        path: 'home',
        component: CxDashboardComponent,
      },
      {
        path: 'inbox',
        redirectTo: 'inbox/me',
      },
      {
        path: 'inbox/me',
        loadChildren: () => import('../inbox/inbox.module').then((m) => m.InboxModule),
      },
      {
        path: 'inbox/all',
        loadChildren: () => import('../inbox/inbox.module').then((m) => m.InboxModule),
      },
      {
        path: 'inbox/following',
        loadChildren: () => import('../inbox/inbox.module').then((m) => m.InboxModule),
      },
      {
        path: 'inbox/collaborating-on',
        loadChildren: () => import('../inbox/inbox.module').then((m) => m.InboxModule),
      },
      {
        path: 'inbox/unassigned',
        loadChildren: () => import('../inbox/inbox.module').then((m) => m.InboxModule),
      },
      {
        path: 'inbox/closed',
        loadChildren: () => import('../inbox/inbox.module').then((m) => m.InboxModule),
      },
      {
        path: 'inbox/escalations',
        loadChildren: () => import('../inbox/inbox.module').then((m) => m.InboxModule),
      },
      {
        path: `inbox/${INBOX_TYPES.INTAKE}`,
        loadChildren: () => import('../inbox/inbox.module').then((m) => m.InboxModule),
      },
      {
        path: 'inbox/email/:ruleId',
        loadChildren: () => import('../inbox/inbox.module').then((m) => m.InboxModule),
      },
      {
        path: 'inbox/chat/:widgetId',
        loadChildren: () => import('../inbox/inbox.module').then((m) => m.InboxModule),
      },
      {
        path: 'inbox/microsoft-teams/:teamsId',
        loadChildren: () => import('../inbox/inbox.module').then((m) => m.InboxModule),
      },
      {
        path: 'inbox/filter/:savedFilterId',
        loadChildren: () => import('../inbox/inbox.module').then((m) => m.InboxModule),
      },
      // We could use a router param here instead of declaring each one
      // We just need to remove the `/inbox/:conversationId` path first
      {
        path: 'inbox/me/conversations/:conversationId',
        loadComponent: () =>
          import('../conversation-details/conversation-details.component').then((m) => m.ConversationDetailsComponent),
      },
      {
        path: 'inbox/me/conversations/:conversationId/files/:fileId',
        loadComponent: () =>
          import('../conversation-details/conversation-file-viewer.component').then(
            (m) => m.ConversationFileViewerComponent,
          ),
      },
      {
        path: 'inbox/all/conversations/:conversationId',
        loadComponent: () =>
          import('../conversation-details/conversation-details.component').then((m) => m.ConversationDetailsComponent),
      },
      {
        path: 'inbox/all/conversations/:conversationId/files/:fileId',
        loadComponent: () =>
          import('../conversation-details/conversation-file-viewer.component').then(
            (m) => m.ConversationFileViewerComponent,
          ),
      },
      {
        path: 'inbox/following/conversations/:conversationId',
        loadComponent: () =>
          import('../conversation-details/conversation-details.component').then((m) => m.ConversationDetailsComponent),
      },
      {
        path: 'inbox/following/conversations/:conversationId/files/:fileId',
        loadComponent: () =>
          import('../conversation-details/conversation-file-viewer.component').then(
            (m) => m.ConversationFileViewerComponent,
          ),
      },
      {
        path: 'inbox/collaborating-on/conversations/:conversationId',
        loadComponent: () =>
          import('../conversation-details/conversation-details.component').then((m) => m.ConversationDetailsComponent),
      },
      {
        path: 'inbox/collaborating-on/conversations/:conversationId/files/:fileId',
        loadComponent: () =>
          import('../conversation-details/conversation-file-viewer.component').then(
            (m) => m.ConversationFileViewerComponent,
          ),
      },
      {
        path: 'inbox/unassigned/conversations/:conversationId',
        loadComponent: () =>
          import('../conversation-details/conversation-details.component').then((m) => m.ConversationDetailsComponent),
      },
      {
        path: 'inbox/unassigned/conversations/:conversationId/files/:fileId',
        loadComponent: () =>
          import('../conversation-details/conversation-file-viewer.component').then(
            (m) => m.ConversationFileViewerComponent,
          ),
      },
      {
        path: 'inbox/closed/conversations/:conversationId',
        loadComponent: () =>
          import('../conversation-details/conversation-details.component').then((m) => m.ConversationDetailsComponent),
      },
      {
        path: 'inbox/closed/conversations/:conversationId/files/:fileId',
        loadComponent: () =>
          import('../conversation-details/conversation-file-viewer.component').then(
            (m) => m.ConversationFileViewerComponent,
          ),
      },
      {
        path: 'inbox/escalations/conversations/:conversationId',
        loadComponent: () =>
          import('../conversation-details/conversation-details.component').then((m) => m.ConversationDetailsComponent),
      },
      {
        path: 'inbox/escalations/conversations/:conversationId/files/:fileId',
        loadComponent: () =>
          import('../conversation-details/conversation-file-viewer.component').then(
            (m) => m.ConversationFileViewerComponent,
          ),
      },
      {
        path: `inbox/${INBOX_TYPES.INTAKE}/conversations/:conversationId`,
        loadComponent: () =>
          import('../conversation-details/conversation-details.component').then((m) => m.ConversationDetailsComponent),
      },
      {
        path: `inbox/${INBOX_TYPES.INTAKE}/conversations/:conversationId/files/:fileId`,
        loadComponent: () =>
          import('../conversation-details/conversation-file-viewer.component').then(
            (m) => m.ConversationFileViewerComponent,
          ),
      },
      {
        path: 'inbox/email/:ruleId/conversations/:conversationId',
        loadComponent: () =>
          import('../conversation-details/conversation-details.component').then((m) => m.ConversationDetailsComponent),
      },
      {
        path: 'inbox/email/:ruleId/conversations/:conversationId/files/:fileId',
        loadComponent: () =>
          import('../conversation-details/conversation-file-viewer.component').then(
            (m) => m.ConversationFileViewerComponent,
          ),
      },
      {
        path: 'inbox/chat/:widgetId/conversations/:conversationId',
        loadComponent: () =>
          import('../conversation-details/conversation-details.component').then((m) => m.ConversationDetailsComponent),
      },
      {
        path: 'inbox/chat/:widgetId/conversations/:conversationId/files/:fileId',
        loadComponent: () =>
          import('../conversation-details/conversation-file-viewer.component').then(
            (m) => m.ConversationFileViewerComponent,
          ),
      },
      {
        path: 'inbox/microsoft-teams/:teamsId/conversations/:conversationId',
        loadComponent: () =>
          import('../conversation-details/conversation-details.component').then((m) => m.ConversationDetailsComponent),
      },
      {
        path: 'inbox/microsoft-teams/:teamsId/conversations/:conversationId/files/:fileId',
        loadComponent: () =>
          import('../conversation-details/conversation-file-viewer.component').then(
            (m) => m.ConversationFileViewerComponent,
          ),
      },
      {
        path: 'inbox/filter/:savedFilterId/conversations/:conversationId',
        loadComponent: () =>
          import('../conversation-details/conversation-details.component').then((m) => m.ConversationDetailsComponent),
      },
      {
        path: 'inbox/filter/:savedFilterId/conversations/:conversationId/files/:fileId',
        loadComponent: () =>
          import('../conversation-details/conversation-file-viewer.component').then(
            (m) => m.ConversationFileViewerComponent,
          ),
      },
      {
        path: 'accounts',
        loadComponent: () => import('../accounts/accounts-list/accounts-list.page').then((m) => m.AccountsListPage),
      },
      {
        path: 'channels',
        redirectTo: 'channels/slack',
      },
      {
        path: 'channels/slack',
        loadComponent: () => import('../channels/channel-list/channel-list.page').then((m) => m.ChannelListPage),
      },
      {
        path: 'channels/email',
        loadComponent: () => import('../channels/channel-list/channel-list.page').then((m) => m.ChannelListPage),
      },
      {
        path: 'channels/microsoft-teams',
        loadComponent: () => import('../channels/channel-list/channel-list.page').then((m) => m.ChannelListPage),
      },
      {
        path: 'channels/in-app-chat',
        loadComponent: () => import('../channels/channel-list/channel-list.page').then((m) => m.ChannelListPage),
      },
      {
        path: 'channels/new',
        loadChildren: () =>
          import('../customers/customer-create/customer-create.module').then((m) => m.CustomerCreatePageModule),
      },
      {
        path: 'channels/:channelId',
        loadChildren: () =>
          import('../channels/channel-admin/channel-admin.page.routes').then((m) => m.ChannelAdminPageRoutes),
      },
      {
        path: 'customers',
        loadChildren: () =>
          import('../customers/customer-list/customer-list.module').then((m) => m.CustomerListPageModule),
      },
      {
        path: 'customers/connect',
        loadChildren: () =>
          import('../customers/customer-create/customer-create.module').then((m) => m.CustomerCreatePageModule),
      },
      {
        path: 'customers/new',
        loadChildren: () =>
          import('../customers/customer-create/customer-create.module').then((m) => m.CustomerCreatePageModule),
      },
      {
        path: 'customers/add',
        loadChildren: () =>
          import('../customers/customer-admin/customer-admin.module').then((m) => m.CustomerAdminPageModule),
      },
      {
        path: 'customers/:customerId',
        loadChildren: () =>
          import('../customers/customer-admin/customer-admin.module').then((m) => m.CustomerAdminPageModule),
      },
      {
        path: 'people',
        loadChildren: () => import('../admin/admin-people/admin-people.module').then((m) => m.AdminPeopleModule),
      },
      {
        path: 'people/:profileId',
        loadChildren: () => import('../profile/profile.module').then((m) => m.ProfilePageModule),
        canActivate: [CompanyPrivacyGuard, AuthGuard],
      },
      {
        path: 'people/:type/:profileId',
        loadChildren: () => import('../profile/profile.module').then((m) => m.ProfilePageModule),
        canActivate: [CompanyPrivacyGuard, AuthGuard],
      },
      {
        path: 'outbound/templates',
        loadChildren: () =>
          import('../admin/admin-templates/admin-templates.module').then((m) => m.AdminTemplatesModule),
      },
      {
        path: 'outbound/broadcasts',
        loadChildren: () =>
          import('../admin/admin-messaging/admin-messaging.module').then((m) => m.AdminMessagingModule),
      },
      {
        path: 'outbound/surveys',
        loadChildren: () => import('../admin/admin-surveys/admin-surveys.module').then((m) => m.AdminSurveysModule),
      },
      {
        path: 'outbound/messages',
        loadChildren: () =>
          import('../admin/admin-messaging/admin-messaging.module').then((m) => m.AdminMessagingModule),
      },
      {
        path: 'outbound',
        redirectTo: 'outbound/broadcasts',
      },
      {
        path: 'knowledge',
        loadComponent: () =>
          import('../knowledge-base/knowledge-base-layout/knowledge-base-layout.component').then(
            (m) => m.KnowledgeBaseLayoutComponent,
          ),
        children: [
          {
            path: 'articles',
            loadComponent: () =>
              import('../knowledge-base/knowledge-base-hierarchy/knowledge-base-hierarchy-page.component').then(
                (m) => m.KnowledgeBaseHierarchyPageComponent,
              ),
          },
          {
            path: 'suggestions',
            loadComponent: () =>
              import('../knowledge-base/knowledge-base-suggestions/knowledge-base-suggestions.component').then(
                (m) => m.KnowledgeBaseSuggestionsComponent,
              ),
          },
          {
            path: 'sources',
            loadComponent: () =>
              import('../knowledge-base/knowledge-base-scrape-jobs/knowledge-base-scrape-jobs.component').then(
                (m) => m.KnowledgeBaseScrapeJobsComponent,
              ),
          },
          {
            path: 'insights',
            loadComponent: () =>
              import('../knowledge-base/knowledge-gap-insights/knowledge-gap-insights.component').then(
                (m) => m.KnowledgeGapInsightsComponent,
              ),
            canActivate: [knowledgeGapAnalysisGuard],
          },
          {
            path: '',
            redirectTo: 'articles',
            pathMatch: 'full',
          },
        ],
      },
      {
        path: 'knowledge/articles/new',
        loadComponent: () =>
          import('../knowledge-base/knowledge-base-article-edit/knowledge-base-article-edit.component').then(
            (m) => m.KnowledgeBaseArticleEditComponent,
          ),
        canActivate: [CompanyPrivacyGuard, AuthGuard],
      },
      {
        path: 'knowledge/articles/:id/edit',
        loadComponent: () =>
          import('../knowledge-base/knowledge-base-article-edit/knowledge-base-article-edit.component').then(
            (m) => m.KnowledgeBaseArticleEditComponent,
          ),
        canActivate: [CompanyPrivacyGuard, AuthGuard],
      },
      {
        path: 'analytics/volume',
        loadChildren: () =>
          import('../admin/admin-analytics/admin-analytics.module').then((m) => m.AdminAnalyticsModule),
      },
      {
        path: 'analytics/performance/reports/:reportId',
        loadChildren: () =>
          import('../analytics/analytics-response-times/analytics-response-times.module').then(
            (m) => m.AnalyticsResponseTimesModule,
          ),
      },
      {
        path: 'analytics/performance',
        loadChildren: () =>
          import('../analytics/analytics-response-times/analytics-response-times.module').then(
            (m) => m.AnalyticsResponseTimesModule,
          ),
      },
      {
        path: 'analytics/ai',
        loadComponent: () =>
          import('../analytics/ai-analytics/ai-analytics.component').then((m) => m.AiAnalyticsComponent),
      },
      {
        path: 'analytics/report-builder',
        loadChildren: () =>
          import('../analytics/report-builder/report-builder.module').then((m) => m.ReportBuilderModule),
      },
      {
        path: 'analytics/boards/:boardId',
        loadChildren: () =>
          import('../analytics/report-builder/report-builder.module').then((m) => m.ReportBuilderModule),
      },
      {
        path: 'analytics/monitoring',
        loadComponent: () => import('../analytics/monitoring/monitoring.component').then((m) => m.MonitoringComponent),
      },
      {
        path: 'analytics',
        redirectTo: 'analytics/performance',
      },
      {
        path: 'automations',
        loadComponent: () =>
          import('../automations/automations-list/automations-list.component').then((m) => m.AutomationsListComponent),
      },
      {
        path: 'automations/:automationId',
        loadComponent: () =>
          import('../automations/edit-automation/edit-automation.component').then((m) => m.EditAutomationComponent),
      },
      {
        path: 'settings/user-groups',
        loadComponent: () =>
          import('../user-groups/user-groups-list/user-groups-list.component').then((m) => m.UserGroupsListComponent),
      },
      {
        path: 'settings/general',
        loadChildren: () => import('../admin/admin-settings/admin-settings.module').then((m) => m.AdminSettingsModule),
      },
      {
        path: 'settings/slack',
        loadChildren: () => import('../admin/admin-slack/admin-slack.module').then((m) => m.AdminSlackModule),
      },
      {
        path: 'settings/ai',
        loadChildren: () => import('../admin/admin-ai/admin-ai.module').then((m) => m.AdminAiModule),
      },
      {
        path: 'settings/ai-tester',
        loadChildren: () =>
          import('../admin/admin-ai-tester/admin-ai-tester.module').then((m) => m.AdminAiTesterModule),
      },
      {
        path: 'settings/security',
        loadChildren: () => import('../admin/admin-security/admin-security.module').then((m) => m.AdminSecurityModule),
      },
      {
        path: 'settings/slas',
        loadChildren: () =>
          import('../admin/admin-support-step-rulesets/admin-support-step-rulesets.module').then(
            (m) => m.AdminSupportStepRulesetsModule,
          ),
      },
      {
        path: 'settings/apps',
        loadComponent: () => import('../admin/admin-apps/admin-apps.component').then((m) => m.AdminAppsComponent),
      },
      {
        path: 'settings/admin',
        loadChildren: () => import('../admin/admin-account/admin-accont.module').then((m) => m.AdminAccountModule),
      },
      {
        path: 'settings/service-accounts',
        loadChildren: () =>
          import('../admin/admin-service-accounts/admin-service-accounts.module').then(
            (m) => m.AdminServiceAccountsModule,
          ),
      },
      {
        path: 'settings/webhooks',
        loadChildren: () => import('../admin/admin-webhooks/admin-webhooks.module').then((m) => m.AdminWebhooksModule),
      },
      {
        path: 'settings/projects/:projectId/settings',
        loadComponent: () =>
          import('../projects/edit-project/edit-project.component').then((m) => m.EditProjectComponent),
      },
      {
        path: 'settings/projects/:projectId/members',
        loadComponent: () =>
          import('../project-members/project-members-list/project-members-list.component').then(
            (m) => m.ProjectMembersListComponent,
          ),
      },
      {
        path: 'settings/projects/:projectId/topics',
        loadComponent: () => import('../admin/agent-topics/agent-topics.component').then((m) => m.AgentTopicsComponent),
      },
      {
        path: 'settings/projects/:projectId/ticket-types',
        loadComponent: () =>
          import('../ticket-types/ticket-types-list/ticket-types-list.component').then(
            (m) => m.TicketTypesListComponent,
          ),
      },
      {
        path: 'settings/projects/:projectId/escalations',
        loadComponent: () =>
          import('../escalation-types/escalation-types-list/escalation-types-list.component').then(
            (m) => m.EscalationTypesListComponent,
          ),
      },
      {
        path: 'settings/projects/:projectId/statuses',
        loadComponent: () =>
          import('../sub-statuses/sub-statuses-list/sub-statuses-list.component').then(
            (m) => m.SubStatusesListComponent,
          ),
      },
      {
        path: 'settings/projects/:projectId/email',
        loadChildren: () => import('../admin/project-email/project-email.module').then((m) => m.ProjectEmailModule),
      },
      {
        path: 'settings/projects/:projectId/in-app-chat',
        loadChildren: () => import('../admin/admin-widget/admin-widget.module').then((m) => m.AdminWidgetModule),
      },
      {
        path: 'settings/projects/:projectId/microsoft-teams',
        loadChildren: () =>
          import('../admin/admin-microsoft-teams/admin-microsoft-teams.module').then(
            (m) => m.AdminMicrosoftTeamsModule,
          ),
      },
      {
        path: 'settings/projects/:projectId/service-accounts',
        loadComponent: () =>
          import('../admin/project-service-accounts/project-service-accounts.component').then(
            (m) => m.ProjectServiceAccountsComponent,
          ),
      },
      {
        path: 'settings/project-list',
        loadComponent: () =>
          import('../projects/projects-list/projects-list.component').then((m) => m.ProjectsListComponent),
      },
      {
        path: 'settings/portals',
        loadComponent: () =>
          import('../admin/portals/portals-layout/portals-layout.component').then((m) => m.PortalsBaseLayoutComponent),
        children: [
          {
            path: 'internal',
            loadComponent: () =>
              import('../admin/portals/portals-internal/portals-internal.component').then(
                (m) => m.PortalsInternalComponent,
              ),
          },
          {
            path: 'external',
            loadComponent: () =>
              import('../admin/portals/portals-external/portals-external.component').then(
                (m) => m.PortalsExternalComponent,
              ),
          },
          {
            path: '',
            redirectTo: 'internal',
            pathMatch: 'full',
          },
        ],
      },
      {
        path: 'settings/collaborator-types',
        loadComponent: () =>
          import('../collaborator-types/collaborator-types-list/collaborator-types-list.component').then(
            (m) => m.CollaboratorTypesListComponent,
          ),
      },
      {
        path: 'settings/automations/:automationId',
        loadComponent: () =>
          import('../automations/edit-automation/edit-automation.component').then((m) => m.EditAutomationComponent),
      },
      {
        path: 'settings/automations',
        loadComponent: () =>
          import('../automations/automations-list/automations-list.component').then((m) => m.AutomationsListComponent),
      },
      {
        path: 'settings/projects/:projectId/automations/:automationId',
        loadComponent: () =>
          import('../automations/edit-automation/edit-automation.component').then((m) => m.EditAutomationComponent),
      },
      {
        path: 'settings/projects/:projectId/automations',
        loadComponent: () =>
          import('../automations/automations-list/automations-list.component').then((m) => m.AutomationsListComponent),
      },
      {
        path: 'settings',
        redirectTo: 'settings/general',
      },
      {
        path: 'conversations/:conversationId',
        loadComponent: () =>
          import('../conversation-details/conversation-details.component').then((m) => m.ConversationDetailsComponent),
      },
      {
        path: 'inbound-emails/:inboundEmailId',
        loadChildren: () =>
          import('../display-inbound-email/display-inbound-email.module').then((m) => m.DisplayInboundEmailModule),
      },
      {
        path: 'inbound-app-chat/:inboundAppChatId',
        loadChildren: () =>
          import('../display-inbound-app-chat/display-inbound-app-chat.module').then(
            (m) => m.DisplayInboundAppChatModule,
          ),
      },
      {
        path: 'inbound-microsoft-teams/:microsoftTeamsMessageId',
        loadChildren: () =>
          import('../display-microsoft-teams-message/display-microsoft-teams-message.module').then(
            (m) => m.DisplayMicrosoftTeamsMessageModule,
          ),
      },
      {
        path: 'inbox/:conversationId',
        loadComponent: () =>
          import('../conversation-details/conversation-details.component').then((m) => m.ConversationDetailsComponent),
      },
      {
        path: 'onboarding',
        loadChildren: () => import('../onboarding/onboarding.module').then((m) => m.OnboardingModule),
      },
      {
        path: 'tags',
        loadComponent: () => import('../tags/tags-list/tags-list.component').then((m) => m.TagsListComponent),
      },
      {
        path: '',
        redirectTo: 'home',
        pathMatch: 'full',
      },
    ],
  },
];

@NgModule({
  imports: [RouterModule.forChild(routes)],
  exports: [RouterModule],
})
export class CxHomeRoutingModule {}
